import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Flame, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { PremiumDialog } from "@/components/PremiumDialog";

export const ThisWeek = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(false);

  useEffect(() => {
    api.get("/checkin").then(setData).catch(() => {});
  }, []);

  if (!data) return null;

  if (data.locked)
    return (
      <div className="panel p-8 rise" data-testid="this-week-locked">
        <div className="flex flex-wrap items-center gap-5">
          <Lock className="h-5 w-5 text-accent" strokeWidth={1.6} />
          <div>
            <span className="overline">Weekly check-ins · Loop Pro</span>
            <p className="mt-2 font-display text-xl tracking-tight">
              Keep a streak and get three things to do each week
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Loop nudges you every week so a roadmap doesn't become a bookmark.
            </p>
          </div>
          <button
            onClick={() => setUpsell(true)}
            data-testid="unlock-checkin-button"
            className="ml-auto px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            Unlock with Pro
          </button>
        </div>
        <PremiumDialog open={upsell} onClose={() => setUpsell(false)} />
      </div>
    );

  const checkIn = async () => {
    setBusy(true);
    try {
      const next = await api.post("/checkin");
      setData(next);
      toast.success(`Checked in — ${next.streak} week${next.streak === 1 ? "" : "s"} in a row`);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (key) => {
    const updated = await api.patch(`/roadmap/${data.roadmap_id}/step`, { step_key: key, done: true });
    setData(await api.get("/checkin"));
    if (updated) toast.success("Step done");
  };

  return (
    <div className="panel p-8 rise" data-testid="this-week-card">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <span className="overline">This week · {data.week}</span>
          <p className="mt-3 font-display text-2xl tracking-tight">
            {data.roadmap_id ? data.path_title : "Nothing on your plate yet"}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-6">
          <div className="text-right">
            <p className="font-display text-2xl flex items-center gap-2 justify-end" data-testid="streak-count">
              <Flame className={`h-5 w-5 ${data.streak ? "text-accent" : "text-muted-foreground"}`} strokeWidth={1.6} />
              {data.streak}
            </p>
            <p className="overline">week streak</p>
          </div>
          <button
            onClick={checkIn}
            disabled={busy || data.checked_in_this_week}
            data-testid="checkin-button"
            className="px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-40 hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color,opacity] duration-200"
          >
            {data.checked_in_this_week ? "Checked in" : "Check in for this week"}
          </button>
        </div>
      </div>

      {data.roadmap_id ? (
        <ul className="mt-7 space-y-2" data-testid="this-week-steps">
          {data.this_week_steps.length === 0 && (
            <li className="text-sm text-muted-foreground">
              Every step is done. Generate a new path whenever you're ready.
            </li>
          )}
          {data.this_week_steps.map((s) => (
            <li key={s.key}>
              <button
                onClick={() => toggle(s.key)}
                data-testid={`week-step-${s.key}`}
                className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-sm hover:bg-white/[0.05] transition-colors duration-200"
              >
                <span className="mt-0.5 h-4 w-4 shrink-0 border border-white/25 flex items-center justify-center">
                  <Check className="h-3 w-3 opacity-0" />
                </span>
                <span className="text-sm leading-relaxed">{s.step}</span>
                <span className="ml-auto overline shrink-0 pl-4">{s.phase}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">
          Pick a possibility and Loop will give you three things to do each week.{" "}
          <Link to="/paths" className="text-primary hover:underline" data-testid="this-week-paths-link">
            See possibilities
          </Link>
        </p>
      )}
    </div>
  );
};
